// © 2025 Ananda Kumar. All rights reserved.
// Unauthorized copying of this file, via any medium, is strictly prohibited.

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';

/**
 * Application Initialization Service
 * Ensures critical initialization happens before HTTP requests are made
 * Prevents race conditions between service initialization and interceptors
 */
@Injectable({
  providedIn: 'root'
})
export class AppInitService {
  private initialized$ = new BehaviorSubject<boolean>(false);

  private readonly TENANT_STORAGE_KEY = 'tenantId';
  private tenantId: string = '';

  constructor() { }

  /**
   * Initialize the application
   * Must be called during application bootstrap before any HTTP requests
   */
  initialize(): Observable<void> {
    // Extract tenant ID from URL
    this.tenantId = this.extractTenantIdFromUrl();

    if (this.tenantId) {
      // Store in sessionStorage for interceptor to use
      sessionStorage.setItem(this.TENANT_STORAGE_KEY, this.tenantId);
    }

    // Mark as initialized - safe for HTTP requests to proceed
    this.initialized$.next(true);

    return of(void 0);
  }

  /**
   * Promise-based initialization for APP_INITIALIZER
   */
  initializePromise(): Promise<void> {
    return new Promise((resolve) => {
      this.initialize().subscribe(() => {
        resolve();
      });
    });
  }

  /**
   * Check if application is initialized
   */
  isInitialized(): boolean {
    return this.initialized$.value;
  }

  /**
   * Get initialization state as observable
   */
  getInitializationState(): Observable<boolean> {
    return this.initialized$.asObservable();
  }

  /**
   * Get current tenant ID
   */
  getTenantId(): string {
    return this.tenantId;
  }

  /**
   * Extract tenant ID from URL path
   * e.g., https://example.com/tenantKey/home -> tenantKey
   */
  private extractTenantIdFromUrl(): string {
    const pathSegments = window.location.pathname.split('/').filter(s => s);
    return pathSegments[0] || '';
  }
}
